import React, { useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Modal } from '../Modal';
import FormInput from '../FormInput';
import TranslatedText from '../TranslatedText';
import { useLanguage } from '../../context/LanguageContext';
import { matomoEvent } from '../matomoTracker';
import { setCredentials } from '../Cookies';
import { signUp } from '../Network';
import "../../css/signUpForm.css"
import { getSentence } from '../translations';


const SignUpForm = ({ open, onClose, onSwitchToSignin }) => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  const [mailErrorMessage, setMailErrorMessage] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [tosErrorMessage, setTosErrorMessage] = useState('');

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Check password match
    if (field === 'password') {
      setPasswordMatch(value === formData.confirmPassword);
    } else if (field === 'confirmPassword') {
      setPasswordMatch(value === formData.password);
    }
  };

  const emailIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
    </svg>
  );

  const passwordIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
    </svg>
  );

  async function handleSignUp(mail, password, passwordConfirmation, agreement)
  {
    if (mail === "" || !mail.includes("@") || !mail.includes("."))
    {
      setMailErrorMessage(getSentence(133))
      setPasswordErrorMessage("")
      setTosErrorMessage("")
      setServerErrorMessage("")
      return
    }
    else if (password != passwordConfirmation || password === "")
    {
      setPasswordErrorMessage(getSentence(134))
      setMailErrorMessage("")
      setTosErrorMessage("")
      setServerErrorMessage("")
      return
    }
    else if (!agreement)
    {
      setTosErrorMessage(getSentence(135))
      setMailErrorMessage("")
      setPasswordErrorMessage("")
      setServerErrorMessage("")
      return
    }

    const response = await signUp(mail, password)

    if (response === "account created")
    {
        setMailErrorMessage("")
        setPasswordErrorMessage("")
        setTosErrorMessage("")
        setServerErrorMessage("")
        setCredentials(mail, password, false);
        matomoEvent('account created', "web", mail);
        window.history.replaceState(null, "", "/")
    }
    else
    {
      setServerErrorMessage(response)
      setMailErrorMessage("")
      setPasswordErrorMessage("")
      setTosErrorMessage("")
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      hideCloseButton={true}
      disableBackdrop={true}
      sx={{ zIndex: 1100 }}
    >
      <Box 
        sx={{ 
          '@keyframes modalFadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(20px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
        class="container"
      >
        <IconButton 
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'text.secondary'
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Typography 
          variant="h4" 
          component="h2" 
          align="center"
          sx={{ 
            mb: 3, 
            fontWeight: 700,
            color: 'primary.main'
          }}
        >
          <TranslatedText textId="SignUpTitle" />
        </Typography>
        
        <Box 
          sx={{ 
            width: '80px', 
            height: '4px', 
            bgcolor: 'primary.main', 
            mx: 'auto', 
            mb: 4,
            borderRadius: '2px'
          }} 
        />
        
          <FormInput
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            required
            labelTextId="emailLabel"
            icon={emailIcon}
          />

          {/* Show mailErrorMessage message if it exists */}
          {mailErrorMessage && (
            <Typography class="errorMsg">
              {mailErrorMessage}
            </Typography>
          )}

          <FormInput
            type="password"
            id="psw"
            name="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            required
            labelTextId="pswLabel"
            icon={passwordIcon}
          />

          <FormInput
            type="password"
            id="psw2"
            name="psw2"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            required
            labelTextId="pswConfirmLabel"
            icon={passwordIcon}
            error={!passwordMatch && formData.confirmPassword}
          />
          
          {/* Show passwordErrorMessage message if it exists */}
          {passwordErrorMessage && (
            <Typography class="errorMsg">
              {passwordErrorMessage}
            </Typography>
          )}
          
          <Box 
            sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(25, 118, 210, 0.05)'
            }}
          >
            <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                name="agreement" 
                required 
                checked={agreementChecked}
                onChange={(e) => setAgreementChecked(e.target.checked)}
                style={{ 
                  marginRight: '10px', 
                  marginTop: '4px',
                  accentColor: '#1976d2'
                }} 
              />
              <Typography variant="body2" component="div" color="text.secondary">
                <TranslatedText textId="TOSCheckbox" dangerousHTML />
              </Typography>
            </label>
          </Box>

          {/* Show tosErrorMessage message if it exists */}
          {tosErrorMessage && (
            <Typography class="errorMsg">
              {tosErrorMessage}
            </Typography>
          )}
          
          <input type="hidden" name="navigator_language" id="navigatorLanguage" />

          {/* Show serverErrorMessage message if it exists */}
          {serverErrorMessage && (
            <Typography class="errorMsg" id="serverErrorMessage">
              {serverErrorMessage}
            </Typography>
          )}
          
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth
            disabled={!passwordMatch}
            id="signupButton"
            sx={{ 
              py: 1.5, 
              mt: 2,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: 3,
              textTransform: 'none',
              transition: 'all 0.3s',
              opacity: !passwordMatch ? 0.7 : 1,
              '&:hover': {
                transform: !passwordMatch ? 'none' : 'translateY(-2px)',
                boxShadow: !passwordMatch ? 3 : 5
              }
            }}
            onClick={()=>handleSignUp(formData.email, formData.password, formData.confirmPassword, agreementChecked)}
          >
            <TranslatedText textId="signupButton" />
          </Button>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <TranslatedText textId="alreadyHaveAccount" /> {' '}
              <Typography 
                component="span" 
                variant="body2" 
                sx={{ 
                  color: 'primary.main', 
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={onSwitchToSignin}
              >
                <TranslatedText textId="signin" />
              </Typography>
            </Typography>
          </Box>
      </Box>
    </Modal>
  );
};

export default SignUpForm; 